import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Ban, CheckCircle, Key, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { API, ROLE_OPTIONS, roleOptionValue, roleLabel, ITEMS_PER_PAGE, paginate, getTotalPages, errDetail } from './adminShared';

const EMPTY_FORM = { username: '', hospital_name: '', role: 'Admin', specialty: null, temporary_password: '', first_login: true };

export default function UsersTab({ users, hospitals, reload, authCfg }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  const filtered = users.filter((u) =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.hospital_name.toLowerCase().includes(search.toLowerCase()) ||
    u.role.toLowerCase().includes(search.toLowerCase()) ||
    (u.specialty || '').toLowerCase().includes(search.toLowerCase())
  );
  const paginated = paginate(filtered, page);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); };
  const openEdit = (user) => {
    setEditing(user);
    setForm({
      ...user,
      hospital_name: user.hospital_name || (hospitals.length > 0 ? hospitals[0].hospital_name : ''),
      role: user.role || 'Admin',
      specialty: user.specialty || null,
      temporary_password: '',
      first_login: user.first_login,
    });
    setDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/users/${editing.username}`, { hospital_name: form.hospital_name, role: form.role, specialty: form.role === 'Specialist' ? form.specialty || '' : undefined }, authCfg);
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API}/admin/users`, form, authCfg);
        toast.success('User created successfully');
      }
      setDialog(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (username) => {
    setConfirm({
      title: 'Delete user',
      description: `Delete user "${username}"?`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/users/${username}`, authCfg);
          toast.success('User deleted successfully');
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to delete user'));
        }
      },
    });
  };

  const handleSuspend = (username, hospitalName) => {
    setConfirm({
      title: `Suspend user "${username}"${hospitalName ? ` (${hospitalName})` : ''}`,
      description: 'The user will be:\n• Logged out of any active session immediately\n• Blocked from logging in until reactivated\n• Blocked from submitting new claims',
      confirmLabel: 'Suspend',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/admin/users/${username}/suspend`, {}, authCfg);
          toast.success(`User "${username}" suspended.`);
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to suspend user'));
        }
      },
    });
  };

  const handleUnsuspend = (username) => {
    setConfirm({
      title: `Reactivate user "${username}"`,
      description: 'They will be able to log in and submit claims again.',
      confirmLabel: 'Reactivate',
      onConfirm: async () => {
        try {
          await axios.post(`${API}/admin/users/${username}/unsuspend`, {}, authCfg);
          toast.success(`User "${username}" reactivated.`);
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to reactivate user'));
        }
      },
    });
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!tempPassword || tempPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/${resetUser.username}/reset-password`, { temporary_password: tempPassword }, authCfg);
      toast.success(`Password reset for ${resetUser.username}. User must change password on next login.`);
      setResetDialog(false);
      setTempPassword('');
    } catch (error) {
      toast.error(errDetail(error, 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Users</CardTitle>
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required disabled={!!editing} />
              </div>
              <div>
                <Label>Hospital</Label>
                <Select value={form.hospital_name || (hospitals.length > 0 ? hospitals[0].hospital_name : 'none')} onValueChange={(value) => setForm({ ...form, hospital_name: value })}>
                  <SelectTrigger><SelectValue placeholder="Select hospital" /></SelectTrigger>
                  <SelectContent>
                    {hospitals.length === 0 ? (
                      <SelectItem value="none" disabled>No hospitals available</SelectItem>
                    ) : (
                      hospitals.map((h) => (
                        <SelectItem key={h.hospital_name} value={h.hospital_name}>{h.hospital_name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={roleOptionValue(form)} onValueChange={(value) => { const o = ROLE_OPTIONS.find((x) => x.value === value); setForm({ ...form, role: o.role, specialty: o.specialty }); }}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {ROLE_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value} data-testid={`user-role-option-${o.value}`}>{o.specialty ? o.specialty : roleLabel(o.role)}</SelectItem>))}
                  </SelectContent>
                </Select>
                {form.role === 'Specialist' && <p className="text-xs text-gray-500 mt-1">Specialist doctor — sees only patients referred to a {form.specialty} at their hospital.</p>}
              </div>
              {!editing && (
                <div>
                  <Label>Temporary Password</Label>
                  <Input type="password" value={form.temporary_password} onChange={(e) => setForm({ ...form, temporary_password: e.target.value })} required />
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Search users by username, hospital, or role..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="user-search-input" />
          </div>
          {search && <p className="text-sm text-gray-500 mt-2">Found {filtered.length} user(s)</p>}
        </div>
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left p-3 text-sm font-semibold">Username</th>
              <th className="text-left p-3 text-sm font-semibold">Hospital</th>
              <th className="text-left p-3 text-sm font-semibold">Role</th>
              <th className="text-center p-3 text-sm font-semibold">Status</th>
              <th className="text-center p-3 text-sm font-semibold">First Login</th>
              <th className="text-center p-3 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((user) => (
              <tr key={user.username} className={`border-b hover:bg-gray-50 ${user.status === 'Suspended' ? 'bg-red-50' : ''}`}>
                <td className="p-3 text-sm font-medium">{user.username}</td>
                <td className="p-3 text-sm">{user.hospital_name}</td>
                <td className={`p-3 text-sm ${user.role === 'Specialist' && !user.specialty ? 'text-amber-700 font-medium' : ''}`} data-testid={`user-role-${user.username}`}>{roleLabel(user.role, user.specialty)}</td>
                <td className="p-3 text-center">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    user.status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`} data-testid={`user-status-${user.username}`}>
                    {user.status === 'Suspended' ? 'Suspended' : 'Active'}
                  </span>
                </td>
                <td className="p-3 text-center text-sm">{user.first_login ? 'Yes' : 'No'}</td>
                <td className="p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(user)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {user.username !== 'superadmin' && (
                      <Button variant="ghost" size="sm" onClick={() => { setResetUser(user); setTempPassword(''); setResetDialog(true); }} className="text-orange-600 hover:bg-orange-50" title="Reset Password" data-testid={`reset-password-${user.username}`}>
                        <Key className="w-4 h-4" />
                      </Button>
                    )}
                    {user.username !== 'superadmin' && (
                      user.status === 'Suspended' ? (
                        <Button variant="ghost" size="sm" onClick={() => handleUnsuspend(user.username)} className="text-green-600 hover:bg-green-50" title="Reactivate User" data-testid={`unsuspend-user-${user.username}`}>
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleSuspend(user.username, user.hospital_name)} className="text-amber-600 hover:bg-amber-50" title="Suspend User (Suspicious Activity)" data-testid={`suspend-user-${user.username}`}>
                          <Ban className="w-4 h-4" />
                        </Button>
                      )
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user.username)} className="text-red-600" disabled={user.username === 'superadmin'}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > ITEMS_PER_PAGE && (
          <Pagination currentPage={page} totalPages={getTotalPages(filtered.length)} onPageChange={setPage} />
        )}
      </CardContent>

      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password - {resetUser?.username}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <Label>Temporary Password</Label>
              <Input type="text" value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} placeholder="Enter temporary password" required minLength={6} autoFocus data-testid="reset-password-input" />
              <p className="text-sm text-gray-500 mt-1">User will be required to change this password on next login.</p>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="reset-password-submit">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
